/* eslint-disable max-len */
import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { CheckCircle2 } from 'lucide-react';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';

const DATE_OPTIONS = {
  weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
};

function ApproveRequestModal({ closeModal, request, getData }) {
  const { member } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);
  const [saving, setSaving] = useState(false);
  const startDate = new Date(request.startDate);
  const endDate = new Date(request.endDate);
  const memberName = `${request.member.user.firstName} ${request.member.user.lastName}`;

  const handleApprove = async () => {
    setSaving(true);
    const response = await sendRequest({
      method: 'put',
      url: API.WORKSPACE.approveOffTimeRequest(member.workspaceId, request.id),
    });
    if (response.success) {
      displayNotification({ message: 'Time off request approved successfully.', severity: 'success' });
      getData();
      closeModal();
    } else {
      displayNotification({ message: 'An error occurs while approving data.', severity: 'error' });
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={closeModal} className="flex-row align-c jstfy-c">
      <Box style={{
        width: 512,
        maxWidth: 'calc(100% - 32px)',
        padding: 24,
        background: '#fff',
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
      >
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[#eef5ea] text-[#53A551]">
          <CheckCircle2 size={28} />
        </div>
        <h5 className="text-lg font-semibold leading-none text-ink-900 mb-0">
          Are you sure you want to approve this request?
        </h5>
        <p className="text-sm text-ink-500 leading-relaxed mb-0">
          You&apos;re about to approve the &ldquo;
          <span className="font-semibold text-ink-900">{startDate.toLocaleDateString('en-US', DATE_OPTIONS)}</span>
          {' '}
          to
          {' '}
          <span className="font-semibold text-ink-900">{endDate.toLocaleDateString('en-US', DATE_OPTIONS)}</span>
          &rdquo; requested PTO for &ldquo;
          <span className="font-semibold text-ink-900">{memberName}</span>
          &rdquo;. You can deny this request again if needed.
        </p>
        <div className="flex justify-start items-center gap-2">
          <button
            type="button"
            onClick={closeModal}
            className="inline-flex items-center justify-center px-4 py-2 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={handleApprove}
            className="inline-flex items-center justify-center px-4 py-2 bg-[#53A551] text-white rounded-[8px] text-sm font-semibold hover:bg-[#458a44] disabled:opacity-50 transition"
          >
            {saving ? 'Approving...' : 'Approve'}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

ApproveRequestModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  getData: PropTypes.func.isRequired,
  request: PropTypes.shape({
    id: PropTypes.number.isRequired,
    startDate: PropTypes.string.isRequired,
    endDate: PropTypes.string.isRequired,
    member: PropTypes.shape({
      user: PropTypes.shape({
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
      }),
    }),
  }).isRequired,
};

export default ApproveRequestModal;
